// ADR-0102 §6 (V113-2-2 follow-up): a connection granted before its manifest's scope bump.
//
// When a collector's metric read is refused because the grant predates the manifest's added scopes,
// the run records a typed `needs_regrant` state on the connection (evidence_connectors.config_json
// under REGRANT_CONFIG_KEY). It is never an error: last_error_* and consecutive_failures stay as the
// config checks left them, so the connection's health, Today and the source-health counts are unchanged.
// Only the next run whose metric read completes (`ok`) clears it. Pure helpers; no runtime imports.
import { scopesAddedSince } from './manifest.js';
/** The config_json key holding the state (non-secret; config_json is rebuilt when credentials are replaced). */
export const REGRANT_CONFIG_KEY = 'metrics_regrant';
/** The stored state, or null when absent or malformed. */
export function parseRegrantState(config) {
    const v = config?.[REGRANT_CONFIG_KEY];
    if (!v || typeof v !== 'object' || v.status !== 'needs_regrant')
        return null;
    if (!Array.isArray(v.scopes) || !v.scopes.length || !v.scopes.every((s) => typeof s === 'string'))
        return null;
    if (typeof v.since !== 'string' || typeof v.manifest_version !== 'number')
        return null;
    return { status: 'needs_regrant', scopes: [...v.scopes], manifest_version: v.manifest_version, since: v.since };
}
/** The last run's metric read, stored beside the state (the re-grant drawer reads it after a check). */
export const METRICS_READ_CONFIG_KEY = 'metrics_read';
export function parseMetricsRead(config) {
    const v = config?.[METRICS_READ_CONFIG_KEY];
    if (!v || typeof v !== 'object' || typeof v.at !== 'string' || !['ok', 'refused', 'failed', 'not_read'].includes(String(v.status)))
        return null;
    return { status: v.status, at: v.at };
}
/**
 * The connection's config after a run, for a kind whose manifest added scopes (null otherwise, or when the
 * collector reads no metrics). `refused` sets the state (keeping the first-seen time); ONLY `ok` clears it;
 * `failed` (throttled, 5xx, network) and `not_read` (nothing to read) leave it as it was. The read is recorded too.
 */
export function nextRegrantConfig(config, manifest, read, now) {
    const scopes = manifest ? scopesAddedSince(manifest, 1) : [];
    if (!read || !scopes.length || !manifest)
        return null;
    const next = { ...config, [METRICS_READ_CONFIG_KEY]: { status: read, at: now.toISOString() } };
    if (read === 'ok')
        delete next[REGRANT_CONFIG_KEY];
    else if (read === 'refused') {
        const prev = parseRegrantState(config);
        const state = { status: 'needs_regrant', scopes, manifest_version: manifest.manifestVersion, since: prev?.since ?? now.toISOString() };
        next[REGRANT_CONFIG_KEY] = state;
    }
    return next;
}
/** A manifest scope as the customer's console names it (`aws-action:cloudwatch:ListMetrics` → `cloudwatch:ListMetrics`). */
export function scopeDisplayName(scope) {
    return scope.replace(/^aws-(?:action|policy):/, '');
}
//# sourceMappingURL=regrant.js.map